import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { ENGLISH_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { logger } from '~/modules/utilities/logger';

// Doubles as the localStorage key and the `?orgPickerPilot` query param name.
export const ORG_PICKER_PILOT_KEY = 'orgPickerPilot';
export const ORG_PICKER_PILOT_CHANGE_EVENT = 'org-picker-pilot-change';

const ENROLL_VALUE = 'true';
const UNENROLL_VALUE = 'false';

/**
 * Runs before routerStore.ts is first imported (see index.ts) — that module reads
 * FeatureFlags.FF_ORG_PICKER synchronously at load time to build its route map, so enrollment
 * needs to already be in localStorage for a pilot user's first page load to register /select-org.
 */
export function applyEnrollmentFromUrl(): void {
    const url = new URL(window.location.href);
    const param = url.searchParams.get(ORG_PICKER_PILOT_KEY)?.toLowerCase();
    if (param !== ENROLL_VALUE && param !== UNENROLL_VALUE) {
        return;
    }
    try {
        if (param === ENROLL_VALUE) {
            localStorage.setItem(ORG_PICKER_PILOT_KEY, ENROLL_VALUE);
        } else {
            localStorage.removeItem(ORG_PICKER_PILOT_KEY);
        }
    } catch (err) {
        logger.error('orgPickerPilot: failed to persist enrollment', err);
        return;
    }
    // A live header needs an explicit signal: same-tab localStorage changes do not emit the
    // browser's `storage` event. This keeps pilot enrollment reactive without polling.
    window.dispatchEvent(new CustomEvent(ORG_PICKER_PILOT_CHANGE_EVENT));
    // Drop the one-shot param so it can't replay enrollment/un-enrollment on a later refresh or share.
    // This runs before boot_client's dynamic import (see index.ts) — an uncaught throw here (e.g. Safari's
    // History-API SecurityError, or a sandboxed iframe embed) would abort the rest of index.ts and block boot.
    try {
        url.searchParams.delete(ORG_PICKER_PILOT_KEY);
        window.history.replaceState({}, '', url.toString());
    } catch (err) {
        logger.error('orgPickerPilot: failed to strip enrollment param from URL', err);
    }
}

// Deliberately separate from FeatureFlags.FF_ORG_PICKER's own withLocalStorageOverride key
// (feature_flags.ts): that override REPLACES the flag outcome, so a stored 'false' there would
// force the flag off even after a real rollout. This key is additive-only — un-enrolling just
// removes the pilot's extra visibility, it never suppresses FF_ORG_PICKER itself.
function isOrgPickerPilotEnrolled(): boolean {
    try {
        return localStorage.getItem(ORG_PICKER_PILOT_KEY) === ENROLL_VALUE;
    } catch (err) {
        logger.error('orgPickerPilot: failed to read enrollment', err);
        return false;
    }
}

/**
 * Shared org-picker gate: on when (FF_ORG_PICKER OR pilot-enrolled) AND the locale is English.
 * The pilot key is separate from the Unleash flag so enrolled users keep visibility through a
 * future flag rollout/rollback. English-only applies identically on AppExchange and AgentExchange
 * — this is a language gate, not a domain gate, so JP stays excluded on both.
 */
export function isOrgPickerEnabled(): boolean {
    return (FeatureFlags.FF_ORG_PICKER || isOrgPickerPilotEnrolled()) && getUserLanguage() === ENGLISH_LANGUAGE_CODE;
}
